import { useEffect, useState } from "react";
import { useWorkflowIntelligence } from "../contexts/WorkflowIntelligenceContext";
import { getUserRole } from "../services/user-role.service";
import { UserRole } from "../types/UserRole";

/**
 * Hard-coded dev allow-list: these users always get Partner as their maximum
 * regardless of what the backend reports. Useful while internal devs are
 * still being added to the appropriate office groups. Empty the array (or
 * delete the override branch) once these accounts are properly mapped.
 */
const DEV_OVERRIDE_EMAILS: readonly string[] = [
  "tomas.borsje@bdo.co.nz",
  "james.mcneil@bdo.co.nz",
];

/**
 * Resolve the caller's maximum allowed role. Applies the dev override first,
 * then falls back to the backend. Returns the raw backend string (or a
 * dev-override value) — callers should pipe the result through
 * `normaliseBackendRoleToUserRole` to get an enum value.
 *
 * Exported so the context's role auto-init can share the same source of
 * truth as this hook — otherwise the dev override would apply only in
 * Settings and not on first sign-in.
 */
export async function resolveMaxRole(email: string | null | undefined): Promise<string> {
  if (email && DEV_OVERRIDE_EMAILS.includes(email.toLowerCase())) {
    return UserRole.Partner;
  }
  return getUserRole();
}

/**
 * Loads the caller's maximum allowed role. The single source of truth for
 * "what roles is this user allowed to hold". Used by:
 *   - Settings, to filter the role-picker dropdown
 *   - Setup, to gate the self-promotion shortcut
 *
 * Returns the raw backend string ("Partner" / "Manager" / "Reviewer") or
 * a dev-override value, or null while loading / on error. Callers should
 * route the value through `canHoldRole` from types/UserRole.ts to make
 * the "is this role allowed?" decision (which handles non-enum values
 * like "Manager" gracefully).
 */
export function useMaxRole(): string | null {
  const { userInfo } = useWorkflowIntelligence();
  const [maxRole, setMaxRole] = useState<string | null>(null);
  const callerEmail = userInfo?.mail || userInfo?.userPrincipalName;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const role = await resolveMaxRole(callerEmail);
        if (!cancelled) setMaxRole(role);
      } catch (error) {
        console.error("Failed to load user max role:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [callerEmail]);

  return maxRole;
}
